// Global keyboard shortcuts (listed in strings.ts and the Keys panel). They stay
// out of the way of form fields: a key typed into a text field, a range or a
// select keeps its usual meaning, and keys with Ctrl, Alt or Meta are left alone.

import type { ViewStore } from "../../state/store";
import type { AudioSession } from "./audio-session";
import type { PowertrainDriver } from "./powertrain-driver";

export interface KeyboardContext {
  store: ViewStore;
  audio: AudioSession;
  driver: PowertrainDriver;
  toggleAudio(): void;
  ensureAudio(): void;
}

const THROTTLE_STEP = 0.05;

function isFormField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.closest("input, select, textarea") !== null;
}

export function bindKeyboard(ctx: KeyboardContext, target: Window = window): () => void {
  const { store, driver } = ctx;
  const throttleBy = (delta: number): void => {
    const t = Math.round((store.state.controls.throttle + delta) * 100) / 100;
    store.set({ controls: { throttle: Math.min(1, Math.max(0, t)) } });
  };

  const down = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey || isFormField(e.target)) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    switch (key) {
      case "a":
        if (!e.repeat) ctx.toggleAudio();
        break;
      case "i":
        if (!e.repeat) {
          const on = !store.state.controls.ignition;
          store.set({ controls: { ignition: on } });
          if (on) ctx.ensureAudio();
        }
        break;
      case "s":
        if (!e.repeat) {
          ctx.ensureAudio();
          driver.setStarter(true);
        }
        break;
      case "ArrowUp":
        throttleBy(THROTTLE_STEP);
        break;
      case "ArrowDown":
        throttleBy(-THROTTLE_STEP);
        break;
      case "0":
        store.set({ controls: { throttle: 0 } });
        break;
      case "m":
        if (!e.repeat) store.set({ audio: { muted: !store.state.audio.muted } });
        break;
      default:
        return;
    }
    e.preventDefault();
  };
  const up = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() === "s") driver.setStarter(false);
  };
  // Letting go of the window (Alt+Tab while cranking) releases the starter.
  const blur = (): void => driver.setStarter(false);

  target.addEventListener("keydown", down);
  target.addEventListener("keyup", up);
  target.addEventListener("blur", blur);
  return () => {
    target.removeEventListener("keydown", down);
    target.removeEventListener("keyup", up);
    target.removeEventListener("blur", blur);
  };
}
